/**
 * @fileoverview Notification Item Component
 * @description Individual notification item display with icon, actions, and navigation
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  ShoppingCart,
  Package,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileText,
  CreditCard,
  Users,
  Calendar,
  Bell,
  X,
  ExternalLink
} from 'lucide-react';
import { useNotificationStore } from '../../stores/notificationStore';

/**
 * Get icon for notification type
 */
const getNotificationIcon = (type) => {
  const iconMap = {
    // Orders
    order_new: ShoppingCart,
    order_updated: ShoppingCart,
    order_cancelled: XCircle,
    payment_received: CreditCard,
    
    // Inventory
    inventory_low: AlertTriangle,
    inventory_out: XCircle,
    inventory_adjusted: Package,
    
    // Sales
    sales_milestone: CheckCircle,
    
    // Documents
    ocr_completed: CheckCircle,
    ocr_failed: XCircle,
    document_uploaded: FileText,
    
    // Subscriptions
    subscription_activated: CheckCircle,
    subscription_expiring: AlertTriangle,
    subscription_expired: XCircle,
    payment_failed: AlertTriangle,
    
    // CA
    ca_invite_received: Users,
    ca_invite_accepted: CheckCircle,
    client_added: Users,
    client_removed: Users,
    
    // Compliance
    compliance_due_soon: Calendar,
    compliance_due_today: AlertTriangle,
    compliance_overdue: XCircle,
    compliance_submitted: CheckCircle,
    
    // Documents (CA)
    client_document_uploaded: FileText,
    document_verified: CheckCircle,
    
    // MIS
    mis_report_ready: FileText,
    client_data_updated: FileText,
    
    // Team
    team_member_invited: Users,
    team_member_joined: Users,
    
    // System
    system_maintenance: Bell,
    feature_update: Bell
  };

  return iconMap[type] || Bell;
};

/**
 * Get color class for notification priority
 */
const getPriorityColor = (priority) => {
  const colorMap = {
    low: 'text-slate-500',
    medium: 'text-blue-600',
    high: 'text-orange-600',
    critical: 'text-red-600'
  };
  return colorMap[priority] || 'text-slate-500';
};

/**
 * Format relative time
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Just now';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString();
};

export const NotificationItem = ({ notification, onMarkAsRead, onDelete }) => {
  const navigate = useNavigate();
  const { markAsRead, deleteNotification } = useNotificationStore();

  const Icon = getNotificationIcon(notification.type);
  const priorityColor = getPriorityColor(notification.priority || 'medium');
  const isUnread = !notification.read;

  const handleClick = async () => {
    // Mark as read if unread
    if (isUnread) {
      try {
        await markAsRead(notification.id);
        if (onMarkAsRead) onMarkAsRead(notification.id);
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }

    // Navigate if action URL is provided
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    try {
      await deleteNotification(notification.id);
      if (onDelete) onDelete(notification.id);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  return (
    <div
      className={cn(
        'px-3 py-3 rounded-xl cursor-pointer transition-colors group/notif',
        isUnread
          ? 'bg-blue-50 hover:bg-blue-100 border-l-2 border-blue-500'
          : 'hover:bg-slate-50'
      )}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn(
          'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center',
          isUnread ? 'bg-blue-100' : 'bg-slate-100',
          priorityColor
        )}>
          <Icon className="w-4 h-4" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className={cn(
                'text-sm font-semibold text-slate-900',
                isUnread && 'font-bold'
              )}>
                {notification.title}
              </p>
              <p className="text-xs font-medium text-slate-600 mt-1 leading-relaxed line-clamp-2">
                {notification.message || notification.body || ''}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  {formatRelativeTime(notification.createdAt || notification.$createdAt)}
                </p>
                {(notification.category || notification.type) && (
                  <>
                    <span className="text-slate-300">•</span>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      {notification.category || notification.type}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover/notif:opacity-100 transition-opacity">
              {notification.actionUrl && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (notification.actionUrl) {
                      navigate(notification.actionUrl);
                    }
                  }}
                  className="p-1 hover:bg-slate-200 rounded transition-colors"
                  title="View"
                >
                  <ExternalLink className="w-3 h-3 text-slate-500" />
                </button>
              )}
              <button
                onClick={handleDelete}
                className="p-1 hover:bg-slate-200 rounded transition-colors"
                title="Delete"
              >
                <X className="w-3 h-3 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Unread indicator */}
          {isUnread && (
            <div className="mt-2 flex items-center gap-1">
              <div className="h-2 w-2 bg-blue-500 rounded-full" />
              <span className="text-[10px] text-blue-600 font-medium">New</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
